import { CheckOutlined, CopyOutlined, EditOutlined } from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import {
  Button,
  Descriptions,
  Empty,
  Flex,
  Table,
  Tag,
  Tooltip,
  Typography,
  theme,
} from "antd";
import { useSelector } from "react-redux";
import { dateTimeValue } from "../../../common/columns";
import { EditTimeEntryModal } from "../../../common/components/EditTimeEntryModal";
import { TableActions } from "../../../common/components/TableActions";
import {
  formatBladeParts,
  formatSessionClipboard,
  type WOClipboardContext,
} from "../../../common/utils/formatSessionClipboard";
import { formatTotalTime } from "../../../common/utils/formatTotalTime";
import type { TimeEntryGroup } from "../../../common/utils/groupTimeEntries";
import { splitTimeEntries } from "../../../common/utils/groupTimeEntries";
import { useCopySession } from "../../../common/utils/useCopySession";
import { buildUserDisplayNameById } from "../../../common/utils/userDisplayName";
import { useTimeEntryEditor } from "../../../common/utils/useTimeEntryEditor";
import type { TimeTracking, UserLimitedList } from "../../generated-axios-client";
import type { RootState } from "../../store";

type Props = {
  intl: IntlShape;
  ticketId: string;
  ticketStatus?: string | null;
  woContext: WOClipboardContext;
};

type SessionGroup = TimeEntryGroup<TimeTracking>;

const TurbineStoppedTag = ({
  value,
  intl,
}: {
  value: string | undefined | null;
  intl: IntlShape;
}) => {
  if (!value) return <Typography.Text type="secondary">—</Typography.Text>;
  return (
    <Tag color={value === "YES" ? "error" : "success"}>
      {value === "YES"
        ? intl.formatMessage({ id: "checkin_turbine_stopped_yes" })
        : intl.formatMessage({ id: "checkin_turbine_stopped_no" })}
    </Tag>
  );
};

const ExpandedRow = ({ record, intl }: { record: TimeTracking; intl: IntlShape }) => {
  const na = <Typography.Text type="secondary">—</Typography.Text>;

  // The session embeds its blades in full, so they need no lookup. Both detail
  // sections show the same list.
  const sessionBlades = (record.blades ?? []).map((sessionBlade) => sessionBlade.blade);
  const bladeText =
    sessionBlades.length > 0 ? (
      <Flex vertical>
        {sessionBlades.map((blade) => (
          <Typography.Text key={blade.id}>{formatBladeParts(blade)}</Typography.Text>
        ))}
      </Flex>
    ) : (
      na
    );

  const checkinItems = [
    {
      key: "blade",
      label: intl.formatMessage({ id: "ticket_session_field_blade" }),
      children: bladeText,
    },
    {
      key: "task",
      label: intl.formatMessage({ id: "ticket_session_field_task" }),
      children: record.task_checkin ?? na,
    },
    {
      key: "progress",
      label: intl.formatMessage({ id: "ticket_session_field_progress" }),
      children: record.progress_checkin ?? na,
    },
    {
      key: "weather",
      label: intl.formatMessage({ id: "ticket_session_field_weather" }),
      children: record.weather_checkin ?? na,
    },
    {
      key: "dam",
      label: intl.formatMessage({ id: "ticket_session_field_dam" }),
      children: record.dam_checkin ?? na,
    },
    {
      key: "df",
      label: intl.formatMessage({ id: "ticket_session_field_df" }),
      children: record.df_checkin ?? na,
    },
    {
      key: "car",
      label: intl.formatMessage({ id: "ticket_session_field_car" }),
      children: record.car_checkin ?? na,
    },
    {
      key: "turbine_stopped",
      label: intl.formatMessage({ id: "ticket_session_field_turbine_stopped" }),
      children: <TurbineStoppedTag value={record.turbine_stopped_checkin} intl={intl} />,
    },
  ];

  const checkoutItems = [
    {
      key: "blade",
      label: intl.formatMessage({ id: "ticket_session_field_blade" }),
      children: bladeText,
    },
    {
      key: "task",
      label: intl.formatMessage({ id: "ticket_session_field_task" }),
      children: record.task_checkout ?? na,
    },
    {
      key: "progress",
      label: intl.formatMessage({ id: "ticket_session_field_progress_checkout" }),
      children: record.progress_checkout ?? na,
    },
    {
      key: "weather",
      label: intl.formatMessage({ id: "ticket_session_field_weather" }),
      children: record.weather_checkout ?? na,
    },
    {
      key: "dam",
      label: intl.formatMessage({ id: "ticket_session_field_dam" }),
      children: record.dam_checkout ?? na,
    },
    {
      key: "df",
      label: intl.formatMessage({ id: "ticket_session_field_df" }),
      children: record.df_checkout ?? na,
    },
    {
      key: "car",
      label: intl.formatMessage({ id: "ticket_session_field_car" }),
      children: record.car_checkout ?? na,
    },
    {
      key: "turbine_stopped",
      label: intl.formatMessage({ id: "ticket_session_field_turbine_stopped" }),
      children: <TurbineStoppedTag value={record.turbine_stopped_checkout} intl={intl} />,
    },
  ];

  return (
    <Flex gap={24} style={{ padding: "8px 32px 8px 48px" }}>
      <Flex vertical style={{ flex: 1 }}>
        <Typography.Text strong style={{ fontSize: 12, marginBottom: 8 }}>
          {intl.formatMessage({ id: "ticket_session_checkin_details" })}
        </Typography.Text>
        <Descriptions
          items={checkinItems}
          size="small"
          column={1}
          bordered={false}
          labelStyle={{ color: "var(--ant-color-text-secondary)", width: 100 }}
        />
      </Flex>
      <Flex vertical style={{ flex: 1 }}>
        <Typography.Text strong style={{ fontSize: 12, marginBottom: 8 }}>
          {record.ended_at
            ? intl.formatMessage({ id: "ticket_session_checkout_details" })
            : intl.formatMessage({ id: "ticket_session_no_checkout_yet" })}
        </Typography.Text>
        {record.ended_at ? (
          <Descriptions
            items={checkoutItems}
            size="small"
            column={1}
            bordered={false}
            labelStyle={{ color: "var(--ant-color-text-secondary)", width: 100 }}
          />
        ) : (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            —
          </Typography.Text>
        )}
      </Flex>
    </Flex>
  );
};

export const TimeSessionsInfo = ({ intl, ticketId, ticketStatus, woContext }: Props) => {
  const { token } = theme.useToken();
  const { copiedId, copySession } = useCopySession();

  const rows = useSelector((state: RootState) => state.ticket.timeSessions);
  const userList = useSelector(
    (state: RootState) => (state.userstate.list.results ?? []) as UserLimitedList[]
  );
  const currentUser = useSelector((state: RootState) => state.userstate.user);
  const editStatus = useSelector((state: RootState) => state.ticket.editTimeEntryStatus);

  const editor = useTimeEntryEditor<TimeTracking>({
    ticketId,
    ticketStatus,
    currentUserId: currentUser?.id,
    currentUserGroups: currentUser?.groups,
    status: editStatus,
  });

  const userMap = buildUserDisplayNameById(userList ?? []);
  const technicianName = (row: TimeTracking) =>
    row.assignee != null ? (userMap[row.assignee] ?? `User ${row.assignee}`) : "—";
  const technicianNames = (group: SessionGroup) => group.rows.map(technicianName);

  const loading = rows === null;
  // Manual task time logs live in the same table but are a separate system with its
  // own tab; only check-in sessions belong here.
  const sessions = loading ? [] : splitTimeEntries(rows).sessions;

  if (!loading && sessions.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={intl.formatMessage({ id: "ticket_time_sessions_empty" })}
      />
    );
  }

  const columns = [
    {
      key: "index",
      title: "#",
      width: 44,
      render: (_: unknown, __: unknown, i: number) => (
        <Typography.Text type="secondary">{i + 1}</Typography.Text>
      ),
    },
    {
      key: "technicians",
      title: intl.formatMessage({ id: "ticket_session_col_technicians" }),
      render: (_: unknown, group: SessionGroup) => (
        <Flex vertical>
          {group.rows.map((row) => (
            <Typography.Text key={row.id}>{technicianName(row)}</Typography.Text>
          ))}
        </Flex>
      ),
    },
    {
      key: "check_in",
      title: intl.formatMessage({ id: "ticket_session_col_check_in" }),
      render: (_: unknown, group: SessionGroup) =>
        group.entry.started_at ? dateTimeValue(group.entry.started_at, intl) : "—",
    },
    {
      key: "check_out",
      title: intl.formatMessage({ id: "ticket_session_col_check_out" }),
      render: (_: unknown, group: SessionGroup) =>
        group.entry.ended_at ? (
          dateTimeValue(group.entry.ended_at, intl)
        ) : (
          <Tag color="success">
            {intl.formatMessage({ id: "ticket_time_session_active" })}
          </Tag>
        ),
    },
    {
      key: "turbine_stopped",
      title: intl.formatMessage({ id: "ticket_session_col_turbine_stopped" }),
      width: 120,
      render: (_: unknown, group: SessionGroup) => (
        <TurbineStoppedTag value={group.entry.turbine_stopped_checkin} intl={intl} />
      ),
    },
    {
      key: "duration",
      title: intl.formatMessage({ id: "ticket_session_col_duration" }),
      render: (_: unknown, group: SessionGroup) => {
        if (!group.entry.started_at) return "—";
        if (group.durationSeconds === null) {
          const diff = (Date.now() - new Date(group.entry.started_at).getTime()) / 1000;
          return (
            <Typography.Text type="secondary">
              {formatTotalTime(diff)} (ongoing)
            </Typography.Text>
          );
        }
        return formatTotalTime(group.durationSeconds);
      },
    },
    {
      key: "copy",
      title: "",
      width: 176,
      render: (_: unknown, group: SessionGroup) => {
        const record = group.entry;
        const sessionData = {
          ...woContext,
          selectedBlades: (record.blades ?? []).map(
            (sessionBlade) => sessionBlade.blade
          ),
          startedAt: record.started_at,
          endedAt: record.ended_at,
          taskCheckin: record.task_checkin,
          damCheckin: record.dam_checkin,
          dfCheckin: record.df_checkin,
          weatherCheckin: record.weather_checkin,
          progressCheckin: record.progress_checkin,
          carCheckin: record.car_checkin,
          taskCheckout: record.task_checkout,
          damCheckout: record.dam_checkout,
          dfCheckout: record.df_checkout,
          weatherCheckout: record.weather_checkout,
          progressCheckout: record.progress_checkout,
          turbineStoppedCheckout: record.turbine_stopped_checkout,
          carCheckout: record.car_checkout,
        };
        const hasCheckout = Boolean(record.ended_at);
        const copyButtons = [
          {
            phase: "full" as const,
            label: intl.formatMessage({ id: "ticket_session_copy_full_label" }),
            tooltip: intl.formatMessage({ id: "ticket_session_copy_full_tooltip" }),
            disabled: false,
          },
          {
            phase: "checkin" as const,
            label: intl.formatMessage({ id: "ticket_session_copy_checkin_label" }),
            tooltip: intl.formatMessage({ id: "ticket_session_copy_checkin_tooltip" }),
            disabled: false,
          },
          {
            phase: "checkout" as const,
            label: intl.formatMessage({ id: "ticket_session_copy_checkout_label" }),
            tooltip: intl.formatMessage({
              id: hasCheckout
                ? "ticket_session_copy_checkout_tooltip"
                : "ticket_session_copy_checkout_disabled_tooltip",
            }),
            disabled: !hasCheckout,
          },
        ];
        return (
          <Flex gap={4} justify="flex-end">
            {copyButtons.map((button) => {
              const copyId = `${record.id}:${button.phase}`;
              const isCopied = copiedId === copyId;
              return (
                <Tooltip key={button.phase} title={button.tooltip}>
                  <Button
                    type="text"
                    size="small"
                    disabled={button.disabled}
                    icon={
                      isCopied ? (
                        <CheckOutlined style={{ color: token.colorSuccess }} />
                      ) : (
                        <CopyOutlined />
                      )
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      copySession(
                        copyId,
                        formatSessionClipboard({ ...sessionData, phase: button.phase })
                      );
                    }}
                  >
                    {button.label}
                  </Button>
                </Tooltip>
              );
            })}
          </Flex>
        );
      },
    },
    {
      key: "actions",
      title: "",
      width: 56,
      render: (_: unknown, group: SessionGroup) =>
        editor.canEdit(group) ? (
          <TableActions
            actions={[
              {
                key: "edit",
                icon: <EditOutlined />,
                tooltip: intl.formatMessage({ id: "ticket_time_edit_tooltip" }),
                onClick: () => editor.openEditor(group),
              },
            ]}
          />
        ) : null,
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        dataSource={sessions}
        rowKey={(group) => group.key}
        loading={loading}
        size="small"
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        expandable={{
          expandedRowRender: (group: SessionGroup) => (
            <ExpandedRow record={group.entry} intl={intl} />
          ),
        }}
      />
      {editor.editing && (
        <EditTimeEntryModal
          open
          mode="session"
          startedAt={editor.editing.entry.started_at}
          endedAt={editor.editing.entry.ended_at}
          title={intl.formatMessage({ id: "ticket_time_edit_title_session" })}
          technicianNames={technicianNames(editor.editing)}
          loading={editor.loading}
          intl={intl}
          onSubmit={editor.submitEdit}
          onCancel={editor.closeEditor}
        />
      )}
    </>
  );
};
